import { z } from 'zod';
import { UUID_PATTERN } from './common';

export const ALLOWED_IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AllowedImageContentType = (typeof ALLOWED_IMAGE_CONTENT_TYPES)[number];
export const IMAGE_CONTENT_TYPE_EXTENSIONS = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
} as const satisfies Record<AllowedImageContentType, string>;

/** 원본 파일 상한. 면 사진은 브라우저와 서버 모두 검사한다. */
export const MAX_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024;
export const MAX_PHOTO_PX = 20_000;
export const FACE_PHOTO_SEGMENT = 'faces';
export const FACE_PHOTO_KEY_PATTERN = new RegExp(
  `^products/(${UUID_PATTERN})/${FACE_PHOTO_SEGMENT}/${UUID_PATTERN}\\.(${Object.values(IMAGE_CONTENT_TYPE_EXTENSIONS).join('|')})$`,
);
export const facePhotoKeySchema = z
  .string()
  .regex(FACE_PHOTO_KEY_PATTERN, { message: '면 사진 키 형식이 올바르지 않습니다.' });

/**
 * 면 합성 에셋(ADR-0007 D6·D14) — 마스크·음영·안내선. 면 사진과 같은 상품 폴더 아래 형제 세그먼트에 둔다.
 * 마스크·음영은 알파가 있어야 하므로 PNG·WebP 만, 안내선은 PNG 만 받는다.
 */
export const FACE_MASK_SEGMENT = 'face-masks';
export const FACE_SHADING_SEGMENT = 'face-shadings';
export const FACE_SAFE_AREA_SEGMENT = 'face-safe-areas';
export const ALPHA_IMAGE_CONTENT_TYPES = ['image/png', 'image/webp'] as const;
export const SAFE_AREA_IMAGE_CONTENT_TYPES = ['image/png'] as const;

const productAssetKeyPattern = (
  segment: string,
  contentTypes: readonly AllowedImageContentType[],
): RegExp =>
  new RegExp(
    `^products/(${UUID_PATTERN})/${segment}/${UUID_PATTERN}\\.(${contentTypes.map((type) => IMAGE_CONTENT_TYPE_EXTENSIONS[type]).join('|')})$`,
  );

export const FACE_MASK_KEY_PATTERN = productAssetKeyPattern(
  FACE_MASK_SEGMENT,
  ALPHA_IMAGE_CONTENT_TYPES,
);
export const FACE_SHADING_KEY_PATTERN = productAssetKeyPattern(
  FACE_SHADING_SEGMENT,
  ALPHA_IMAGE_CONTENT_TYPES,
);
export const FACE_SAFE_AREA_KEY_PATTERN = productAssetKeyPattern(
  FACE_SAFE_AREA_SEGMENT,
  SAFE_AREA_IMAGE_CONTENT_TYPES,
);
export const faceMaskKeySchema = z
  .string()
  .regex(FACE_MASK_KEY_PATTERN, { message: '마스크 키 형식이 올바르지 않습니다.' });
export const faceShadingKeySchema = z
  .string()
  .regex(FACE_SHADING_KEY_PATTERN, { message: '음영 키 형식이 올바르지 않습니다.' });
export const faceSafeAreaKeySchema = z
  .string()
  .regex(FACE_SAFE_AREA_KEY_PATTERN, { message: '안내선 키 형식이 올바르지 않습니다.' });

export const facePhotoSizeSchema = z.strictObject({
  widthPx: z.number().int().min(1).max(MAX_PHOTO_PX),
  heightPx: z.number().int().min(1).max(MAX_PHOTO_PX),
});
export type FacePhotoSize = z.infer<typeof facePhotoSizeSchema>;
